import React, { useState } from 'react';
import { Package, AlertCircle, Plus, ArrowLeftRight } from 'lucide-react';
import { useIngredients } from './hooks/useIngredients';
import { IngredientsTable } from './components/IngredientsTable';
import { IngredientsFilters } from './components/IngredientsFilters';
import { IngredientModal } from './components/IngredientModal';
import { PriceHistoryModal } from './components/PriceHistoryModal';
import { LocationsModal } from './components/LocationsModal';
import { IngredientSuppliersModal } from './components/IngredientSuppliersModal';
import { TransformationRulesTab } from './components/TransformationRulesTab';
import { ToggleActiveModal } from './components/ToggleActiveModal';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { Ingredient } from '@/types/ingredient.types';

export const IngredientsPage: React.FC = () => {
  
  
  const [ingredientModal, setIngredientModal] = useState<{
    isOpen: boolean;
    ingredient: Ingredient | null;
  }>({ isOpen: false, ingredient: null });
  
  const [priceHistoryModal, setPriceHistoryModal] = useState<{
    isOpen: boolean;
    ingredient: Ingredient | null;
  }>({ isOpen: false, ingredient: null });

  const [locationsModal, setLocationsModal] = useState<{
    isOpen: boolean;
    ingredient: Ingredient | null;
  }>({ isOpen: false, ingredient: null });

  const [suppliersModal, setSuppliersModal] = useState<{
    isOpen: boolean;
    ingredient: Ingredient | null;
  }>({ isOpen: false, ingredient: null });

  const [toggleActiveModal, setToggleActiveModal] = useState<{
    isOpen: boolean;
    ingredient: Ingredient | null;
  }>({ isOpen: false, ingredient: null });

  const [toggleLoading, setToggleLoading] = useState(false);

  const {
    ingredients,
    stats,
    pagination,
    filters,
    loading,
    error,
    updateFilters,
    createIngredient,
    updateIngredient,
    goToPage,
    refresh
  } = useIngredients();


  const handleEditIngredient = (ingredient: Ingredient) => {
    setIngredientModal({ isOpen: true, ingredient });
  };

  const handleToggleActive = (ingredient: Ingredient) => {
    setToggleActiveModal({ isOpen: true, ingredient });
  };

  const handleConfirmToggleActive = async () => {
    if (toggleActiveModal.ingredient) {
      setToggleLoading(true);
      await updateIngredient(toggleActiveModal.ingredient.id, {
        isActive: !toggleActiveModal.ingredient.isActive
      });
      setToggleLoading(false);
      setToggleActiveModal({ isOpen: false, ingredient: null });
    }
  };

  const handleManagePrices = (ingredient: Ingredient) => {
    setPriceHistoryModal({ isOpen: true, ingredient });
  };

  const handleManageLocations = (ingredient: Ingredient) => {
    setLocationsModal({ isOpen: true, ingredient });
  };

  const handleManageSuppliers = (ingredient: Ingredient) => {
    setSuppliersModal({ isOpen: true, ingredient });
  };

  const handleSavePrice = async (price: number, effectiveDate: string, reason?: string) => {
    if (priceHistoryModal.ingredient) {
      // Refresh the ingredients list to show the updated price
      await refresh();
      setPriceHistoryModal({ isOpen: false, ingredient: null });
    }
  };


  const handleSaveIngredient = async (data: Partial<Ingredient>) => {
    if (ingredientModal.ingredient) {
      await updateIngredient(ingredientModal.ingredient.id, data);
    } else {
      await createIngredient(data);
    }
    setIngredientModal({ isOpen: false, ingredient: null });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-700">Cargando insumos...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error al cargar insumos</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Gestión de Insumos</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Catálogo global de ingredientes y reglas de transformación
          </p>
        </div>
        <Button
          onClick={() => setIngredientModal({ isOpen: true, ingredient: null })}
          className="flex items-center gap-2 bg-macartur-primary hover:bg-macartur-primary/90"
        >
          <Plus className="w-4 h-4" />
          Nuevo Insumo
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="ingredients" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ingredients" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Catálogo de Insumos
          </TabsTrigger>
          <TabsTrigger value="transformations" className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4" />
            Reglas de Transformación
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="ingredients" className="space-y-6">
          {/* Filters */}
          <IngredientsFilters 
            filters={filters}
            onFiltersChange={updateFilters}
          />

          {/* Ingredients Table */}
          <div className="bg-white rounded-lg border">
            <IngredientsTable
              ingredients={ingredients}
              onEdit={handleEditIngredient}
              onToggleActive={handleToggleActive}
              onManagePrices={handleManagePrices}
              onManageLocations={handleManageLocations}
              onManageSuppliers={handleManageSuppliers}
            />
            
            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Mostrando {ingredients.length} de {pagination.totalItems} ingredientes
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                  >
                    Anterior
                  </Button>
                  
                  <span className="text-sm text-gray-700">
                    Página {pagination.currentPage} de {pagination.totalPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="transformations">
          <TransformationRulesTab />
        </TabsContent>
      </Tabs>



      {/* Ingredient Modal */}
      <IngredientModal
        isOpen={ingredientModal.isOpen}
        ingredient={ingredientModal.ingredient}
        onClose={() => setIngredientModal({ isOpen: false, ingredient: null })}
        onSave={handleSaveIngredient}
      />

      {/* Price History Modal */}
      <PriceHistoryModal
        isOpen={priceHistoryModal.isOpen}
        ingredient={priceHistoryModal.ingredient}
        onClose={() => setPriceHistoryModal({ isOpen: false, ingredient: null })}
        onSavePrice={handleSavePrice}
      />

      {/* Locations Modal */}
      <LocationsModal
        ingredient={locationsModal.ingredient}
        isOpen={locationsModal.isOpen}
        onClose={() => setLocationsModal({ isOpen: false, ingredient: null })}
      />

      {/* Suppliers Modal */}
      <IngredientSuppliersModal
        open={suppliersModal.isOpen}
        onOpenChange={(open) => !open && setSuppliersModal({ isOpen: false, ingredient: null })}
        ingredient={suppliersModal.ingredient}
      />

      {/* Toggle Active Modal */}
      <ToggleActiveModal
        isOpen={toggleActiveModal.isOpen}
        ingredient={toggleActiveModal.ingredient}
        onClose={() => setToggleActiveModal({ isOpen: false, ingredient: null })}
        onConfirm={handleConfirmToggleActive}
        loading={toggleLoading}
      />
    </div>
  );
};